/**
 * DifyAPIテストの入力方法
 */
export type InputMethod = "file" | "text";

/**
 * DifyAPIテストのジョブ状態
 * queued: 実行待ち
 * running: 実行中
 * completed: 正常完了
 * error: エラー終了
 * cancelled: キャンセル(未実装)
 * 基本遷移: queued → running → completed or error
 */
export type DifyAPITestJobStatus = "queued" | "running" | "completed" | "error" | "cancelled";

/**
 * DifyAPIテストの実行状態
 * running: 実行中
 * completed: 全ジョブ正常完了
 * error: いずれかのジョブでエラー終了
 * cancelled: 実行途中でキャンセル
 * 基本遷移: running → completed or error or cancelled
 */
export type DifyAPITestRunStatus = "running" | "completed" | "error" | "cancelled";

/**
 * フォーム入力値
 * @property inputMethod 入力方法
 * @property draft 入力中のテキスト
 * @property questions 質問一覧
 * @property file 選択中のファイル
 */
export type DifyAPITestFormValues = {
  inputMethod: InputMethod;
  draft: string;
  questions: string[];
  file: File | null;
};

/**
 * API送信用のペイロード
 * @property inputMethod 入力方法
 * @property questions 送信する質問一覧
 */
export type DifySubmitPayload =
  | {
      inputMethod: "text";
      questions: TextQuestionInputItem[];
    }
  | {
      inputMethod: "file";
      questions: CsvQuestionInputItem[];
    };

/**
 * 送信準備結果
 * @property payload 送信可能な場合のペイロード
 * @property validationMessage バリデーションエラーメッセージ
 */
export type DifySubmitPreparationResult = {
  payload: DifySubmitPayload | null;
  validationMessage: string | null;
};

/**
 * テキスト入力の質問項目
 * @property question 質問文
 */
export type TextQuestionInputItem = {
  question: string;
};

/**
 * CSV入力の質問項目
 * @property question 質問文
 * @property expectedAnswer 期待する回答
 */
export type CsvQuestionInputItem = {
  question: string;
  expectedAnswer: string;
};

/**
 * 質問入力項目
 */
export type DifyQuestionInputItem = TextQuestionInputItem | CsvQuestionInputItem;

/**
 * 実行ジョブ
 * @property jobId ジョブID
 * @property sessionId セッションID
 * @property question 質問文
 * @property expectedAnswer 期待する回答
 * @property status ジョブ状態
 * @property chunks 受信した出力片
 * @property aiOutput AI出力全文
 * @property messageId メッセージID
 * @property errorMessage エラーメッセージ
 * @property startedAt 開始日時
 * @property completedAt 完了日時
 */
export type DifyAPITestJob = {
  jobId: string;
  sessionId: string;
  question: string;
  expectedAnswer: string;
  status: DifyAPITestJobStatus;
  chunks: string[];
  aiOutput: string;
  messageId: string;
  errorMessage: string;
  startedAt: Date | null;
  completedAt: Date | null;
};

/**
 * 実行単位
 * 実行単位がrun、質問一覧がjobsに相当
 * @property runId 実行ID
 * @property appName Difyアプリ名
 * @property title 実行タイトル
 * @property inputMethod 入力方法
 * @property status 実行状態
 * @property createdAt 作成日時
 * @property finishedAt 終了日時
 * @property jobs ジョブ一覧
 */
export type DifyAPITestRun = {
  runId: string;
  appName: string;
  title: string;
  inputMethod: InputMethod;
  status: DifyAPITestRunStatus;
  createdAt: Date;
  finishedAt: Date | null;
  jobs: DifyAPITestJob[];
};

/**
 * CSV出力用の行データ
 * @property question 質問文
 * @property expectedAnswer 期待する回答
 * @property aiOutput AI出力
 * @property judgment 判定結果
 * @property note 備考
 */
export type DifyResultCsvRow = {
  question: string;
  expectedAnswer: string;
  aiOutput: string;
  judgment: string;
  messageId: string;
  note: string;
};

/**
 * CSVヘッダー定義
 * @property question 質問列の見出し
 * @property expectedAnswer 正解列の見出し
 * @property aiOutput AI出力列の見出し
 * @property judgment 判定列の見出し
 * @property messageId 会話ID列の見出し
 * @property note 備考列の見出し
 */
export const DIFY_RESULT_CSV_HEADERS = {
  question: "質問",
  expectedAnswer: "正解",
  aiOutput: "AI出力",
  judgment: "正否判定",
  messageId: "会話ID",
  note: "備考",
} as const;

/**
 * 履歴一覧表示用データ
 * @property runId 実行ID
 * @property title 実行タイトル
 * @property createdAt 作成日時
 * @property status 実行状態
 * @property totalCount 総件数
 * @property completedCount 完了件数
 * @property errorCount エラー件数
 * @property csvData CSV出力データ
 */
export type QuestionTableList = {
  runId: string;
  title: string;
  createdAt: Date;
  createdAtLabel: string;
  status: DifyAPITestRunStatus;
  totalCount: number;
  completedCount: number;
  errorCount: number;
  csvData: DifyResultCsvRow[];
};
